/**
 * Masonry Layout Module
 * Pinterest-style layout with dynamic height calculation
 */

/**
 * Masonry layout renderer
 */
export class MasonryLayout {
  /**
   * Creates a masonry layout instance
   * @param {Object} config - Layout configuration
   */
  constructor(config = {}) {
    this.columns = config.columns || 3;
    this.gap = config.gap || 16;
    this.columnHeights = [];
    this.columnElements = [];
    this.openInLightbox = config.openInLightbox !== false;
    this.onItemClick = config.onItemClick || null;
    this.displayLimit = config.displayLimit || null; // Initial posts to display
    this.enableGapFilling = config.enableGapFilling !== false;
    this.allPosts = []; // Store all available posts
    this.displayedPostIndices = new Set(); // Track which posts are displayed
    this.masonryContainer = null; // Store reference to masonry container
  }

  /**
   * Renders the masonry layout
   * @param {Array} posts - Instagram posts data
   * @param {HTMLElement} container - Container element
   * @returns {HTMLElement} Rendered masonry element
   */
  render(posts, container) {
    // Check if mobile and render compact grid instead
    if (this.isMobileView()) {
      return this.renderMobileCompactGrid(posts, container);
    }

    // Store all posts for potential gap filling
    this.allPosts = posts;

    // Determine which posts to display initially
    const postsToDisplay = this.displayLimit && this.displayLimit < posts.length
      ? posts.slice(0, this.displayLimit)
      : posts;

    const masonry = document.createElement('div');
    masonry.className = 'ig-widget-masonry';
    masonry.style.cssText = `
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
    `;

    this.masonryContainer = masonry;

    // Create columns
    const columns = this.createColumns();
    this.columnElements = columns;
    columns.forEach(column => masonry.appendChild(column));

    // Distribute posts across columns
    this.distributePosts(postsToDisplay, columns);

    // Balance column heights after images load
    this.balanceAfterImagesLoad(masonry);

    // Add responsive behavior
    this.addResponsiveStyles(container);

    return masonry;
  }

  /**
   * Checks if current viewport is mobile
   * @returns {boolean}
   */
  isMobileView() {
    return window.innerWidth <= 640;
  }

  /**
   * Renders compact 1x2 grid for mobile
   * @param {Array} posts - Instagram posts data
   * @param {HTMLElement} container - Container element
   * @returns {HTMLElement} Rendered mobile grid
   */
  renderMobileCompactGrid(posts, container) {
    const grid = document.createElement('div');
    grid.className = 'ig-widget-mobile-compact-grid';
    grid.style.cssText = `
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      width: 100%;
      padding: 10px;
      box-sizing: border-box;
    `;

    // Get first 2 posts only (side by side)
    const displayPosts = posts.slice(0, 2);

    displayPosts.forEach((post, index) => {
      const card = this.createMobileCompactCard(post, index);
      grid.appendChild(card);
    });

    return grid;
  }

  /**
   * Creates a compact mobile card with play button
   * @param {Object} post - Instagram post data
   * @param {number} index - Post index
   * @returns {HTMLElement} Mobile card element
   */
  createMobileCompactCard(post, index) {
    const card = document.createElement('div');
    card.className = 'ig-widget-mobile-compact-card';
    card.setAttribute('data-post-index', index);

    if (this.onItemClick) {
      card.style.cursor = 'pointer';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.addEventListener('click', () => this.onItemClick(index));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.onItemClick(index);
        }
      });
    }

    card.style.cssText = `
      position: relative;
      aspect-ratio: 9/16;
      border-radius: 12px;
      overflow: hidden;
      background: #000;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    `;

    const thumbnail = post.thumbnail_url || post.media_url;

    card.innerHTML = `
      <img
        src="${thumbnail}"
        alt="${this.escapeHtml(post.caption || 'Instagram reel')}"
        style="
          width: 100%;
          height: 100%;
          object-fit: cover;
        "
      />
      <div style="
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        ">
          <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 10.268C19.3333 11.0378 19.3333 12.9623 18 13.7321L3 22.1244C1.66667 22.8942 -1.19209e-06 21.9319 -1.10313e-06 20.3923L-2.21695e-07 3.60769C-1.32737e-07 2.06809 1.66667 1.10583 3 1.87564L18 10.268Z" fill="#333"/>
          </svg>
        </div>
      </div>
    `;

    // Add hover effect
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'scale(1.05)';
      card.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.25)';
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'scale(1)';
      card.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
    });

    return card;
  }

  /**
   * Creates column elements
   * @returns {Array<HTMLElement>} Array of column elements
   */
  createColumns() {
    const columns = [];
    this.columnHeights = new Array(this.columns).fill(0);

    for (let i = 0; i < this.columns; i++) {
      const column = document.createElement('div');
      column.className = `ig-widget-masonry-column ig-widget-masonry-column-${i}`;
      column.style.cssText = `
        display: flex;
        flex-direction: column;
        width: calc((100% - ${(this.columns - 1) * this.gap}px) / ${this.columns});
        margin-left: ${i > 0 ? this.gap : 0}px;
      `;
      columns.push(column);
    }

    return columns;
  }

  /**
   * Distributes posts using fill-to-height algorithm with guaranteed column coverage
   * First column gets 2 tallest posts (sets target height)
   * Each other column is guaranteed at least 1 post
   * Remaining posts fill columns until they match first column height
   * @param {Array} posts - Instagram posts data
   * @param {Array<HTMLElement>} columns - Column elements
   */
  distributePosts(posts, columns) {
    // Calculate estimated heights for all posts
    const postsWithHeights = posts.map((post, index) => ({
      post,
      originalIndex: index,
      estimatedHeight: this.estimateItemHeight(post)
    }));

    // Sort by estimated height (tallest first)
    postsWithHeights.sort((a, b) => b.estimatedHeight - a.estimatedHeight);

    // Step 1: Place 2 tallest posts in first column
    const firstColumnPosts = postsWithHeights.slice(0, 2);
    firstColumnPosts.forEach(({ post, originalIndex, estimatedHeight }) => {
      const item = this.createMasonryItem(post, originalIndex);
      columns[0].appendChild(item);
      this.displayedPostIndices.add(originalIndex);
      this.columnHeights[0] += estimatedHeight + this.gap;
    });

    const targetHeight = this.columnHeights[0];
    const remainingPosts = postsWithHeights.slice(2);

    // Step 2: Guarantee each column gets at least 1 post
    // This ensures all columns render content before fill-to-height balancing
    let postIndex = 0;
    const minPostsPerColumn = 1;

    for (let colIndex = 1; colIndex < this.columns && postIndex < remainingPosts.length; colIndex++) {
      for (let i = 0; i < minPostsPerColumn && postIndex < remainingPosts.length; i++) {
        const { post, originalIndex, estimatedHeight } = remainingPosts[postIndex];

        const item = this.createMasonryItem(post, originalIndex);
        columns[colIndex].appendChild(item);
        this.displayedPostIndices.add(originalIndex);
        this.columnHeights[colIndex] += estimatedHeight + this.gap;

        postIndex++;
      }
    }

    // Step 3: Distribute remaining posts to shortest column (excluding column 0)
    // Column 0 is the reference column and should keep only its initial posts
    while (postIndex < remainingPosts.length) {
      // Find shortest column among columns 1+
      const otherColumnHeights = this.columnHeights.slice(1);
      const minHeight = Math.min(...otherColumnHeights);
      const columnIndex = otherColumnHeights.indexOf(minHeight) + 1; // +1 to account for slice

      const { post, originalIndex, estimatedHeight } = remainingPosts[postIndex];
      const item = this.createMasonryItem(post, originalIndex);
      columns[columnIndex].appendChild(item);
      this.displayedPostIndices.add(originalIndex);
      this.columnHeights[columnIndex] += estimatedHeight + this.gap;

      postIndex++;
    }
  }

  /**
   * Estimates item height based on media type and aspect ratio
   * @param {Object} post - Instagram post data
   * @returns {number} Estimated height in pixels
   */
  estimateItemHeight(post) {
    // Base height is 250px, adjust for different media types
    let baseHeight = 250;

    // Videos and carousels tend to be taller (4:5 aspect ratio)
    if (post.media_type === 'VIDEO' || post.media_type === 'CAROUSEL_ALBUM') {
      baseHeight = 300;
    }

    // Add caption height if exists
    if (post.caption && post.caption.length > 50) {
      baseHeight += 60; // Additional height for caption
    }

    return baseHeight;
  }

  /**
   * Creates a single masonry item
   * @param {Object} post - Instagram post data
   * @param {number} index - Item index
   * @returns {HTMLElement} Masonry item element
   */
  createMasonryItem(post, index) {
    const item = document.createElement(this.openInLightbox ? 'div' : 'a');
    item.className = 'ig-widget-masonry-item';
    item.setAttribute('data-post-index', index);

    if (this.openInLightbox) {
      item.style.cursor = 'pointer';
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', `View ${post.caption || 'Instagram post'}`);

      // Click handler for lightbox
      item.addEventListener('click', () => {
        if (this.onItemClick) {
          this.onItemClick(index);
        }
      });

      // Keyboard support
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.onItemClick) {
            this.onItemClick(index);
          }
        }
      });
    } else {
      item.href = post.permalink;
      item.target = '_blank';
      item.rel = 'noopener noreferrer';
      item.setAttribute('aria-label', post.caption || 'Instagram post');
    }

    item.style.cssText = `
      display: block;
      margin-bottom: ${this.gap}px;
      text-decoration: none;
      color: inherit;
      border-radius: 8px;
      overflow: hidden;
      background: var(--ig-widget-bg-secondary, #f5f5f5);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    `;

    const mediaUrl = post.media_type === 'VIDEO'
      ? (post.thumbnail_url || post.media_url)
      : post.media_url;

    // For videos, create both thumbnail and video element
    const isVideo = post.media_type === 'VIDEO';
    const videoHtml = isVideo ? `
      <video
        class="ig-widget-masonry-video"
        src="${post.media_url}"
        muted
        loop
        playsinline
        preload="metadata"
        style="
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        "
      ></video>
    ` : '';

    item.innerHTML = `
      <div class="ig-widget-masonry-image" style="
        position: relative;
        width: 100%;
      ">
        <img
          class="ig-widget-masonry-thumbnail"
          src="${mediaUrl}"
          alt="${this.escapeHtml(post.caption || 'Instagram post')}"
          loading="lazy"
          style="
            width: 100%;
            height: auto;
            display: block;
            ${isVideo ? 'transition: opacity 0.3s ease;' : ''}
          "
        />
        ${videoHtml}
        ${post.media_type === 'VIDEO' ? '<div class="ig-widget-video-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5); pointer-events: none; z-index: 2; transition: opacity 0.3s ease;">▶</div>' : ''}
        ${post.media_type === 'CAROUSEL_ALBUM' ? '<div class="ig-widget-carousel-icon" style="position: absolute; top: 12px; right: 12px; color: white; font-size: 24px; text-shadow: 0 2px 4px rgba(0,0,0,0.5);">⋮⋯</div>' : ''}
      </div>
      ${post.caption ? `<div class="ig-widget-masonry-caption" data-has-caption="true" style="
        padding: 12px;
        font-size: 14px;
        line-height: 1.5;
        color: var(--ig-widget-text-primary, #333);
        background: white;
      ">
        <p class="ig-masonry-caption-text" style="
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        ">${this.escapeHtml(post.caption)}</p>
      </div>` : ''}
    `;

    // Add hover effect
    item.addEventListener('mouseenter', () => {
      item.style.transform = 'translateY(-4px)';
      item.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
    });

    item.addEventListener('mouseleave', () => {
      item.style.transform = 'translateY(0)';
      item.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
    });

    // Add video hover-to-play functionality
    if (isVideo) {
      const videoElement = item.querySelector('.ig-widget-masonry-video');
      const thumbnail = item.querySelector('.ig-widget-masonry-thumbnail');
      const playIcon = item.querySelector('.ig-widget-video-icon');

      if (videoElement && thumbnail) {
        const mediaContainer = item.querySelector('.ig-widget-masonry-image');
        let hoverTimeout = null;

        mediaContainer.addEventListener('mouseenter', () => {
          // Clear any pending leave timeout
          if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            hoverTimeout = null;
          }

          // Fade out thumbnail and play icon, fade in and play video
          thumbnail.style.opacity = '0';
          if (playIcon) playIcon.style.opacity = '0';

          // Wait for fade out, then play video
          setTimeout(() => {
            videoElement.style.opacity = '1';
            videoElement.play().catch(err => {
              // Autoplay might be blocked, silently fail
              console.debug('Video autoplay blocked:', err);
            });
          }, 150);
        });

        mediaContainer.addEventListener('mouseleave', () => {
          // Add small delay to prevent rapid toggling
          hoverTimeout = setTimeout(() => {
            // Pause video and fade out
            videoElement.pause();
            videoElement.currentTime = 0;
            videoElement.style.opacity = '0';

            // Fade in thumbnail and play icon
            thumbnail.style.opacity = '1';
            if (playIcon) playIcon.style.opacity = '1';
          }, 100);
        });
      }
    }

    // Use Intersection Observer for lazy loading animation
    if ('IntersectionObserver' in window) {
      item.style.opacity = '0';
      item.style.transform = 'translateY(20px)';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              item.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
              item.style.opacity = '1';
              item.style.transform = 'translateY(0)';
            }, index * 50); // Stagger animation
            observer.unobserve(item);
          }
        });
      }, { threshold: 0.1 });

      observer.observe(item);
    } else {
      item.style.opacity = '1';
    }

    return item;
  }

  /**
   * Waits for all images to load, then balances column heights
   * @param {HTMLElement} masonry - Masonry container
   */
  balanceAfterImagesLoad(masonry) {
    const images = masonry.querySelectorAll('img');

    if (images.length === 0) {
      // No images, balance immediately
      setTimeout(() => this.balanceColumnHeights(), 50);
      return;
    }

    let loadedCount = 0;
    const totalImages = images.length;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        // All images loaded, now balance
        setTimeout(() => this.balanceColumnHeights(), 100);
      }
    };

    images.forEach(img => {
      if (img.complete) {
        checkAllLoaded();
      } else {
        img.addEventListener('load', checkAllLoaded);
        img.addEventListener('error', checkAllLoaded); // Count errors too
      }
    });

    // Fallback: balance after 2 seconds regardless
    setTimeout(() => {
      if (loadedCount < totalImages) {
        this.balanceColumnHeights();
      }
    }, 2000);
  }

  /**
   * Balances column heights by expanding captions in shorter columns
   * If gap filling is enabled and tallest column is fully truncated, adds more posts
   * Maintains minimum 3 lines, only expands upward for balance
   */
  balanceColumnHeights() {
    if (!this.columnElements || this.columnElements.length === 0) return;

    // First, ensure all captions have minimum 3 lines
    this.columnElements.forEach(column => {
      const captions = column.querySelectorAll('.ig-masonry-caption-text');
      captions.forEach(caption => {
        const currentClamp = parseInt(caption.style.webkitLineClamp) || 3;
        if (currentClamp < 3) {
          caption.style.webkitLineClamp = '3';
        }
      });
    });

    // Let the DOM settle after setting minimums
    setTimeout(() => {
      // Get actual column heights after minimum is set
      const heights = this.columnElements.map(col => col.offsetHeight);
      const maxHeight = Math.max(...heights);
      const maxHeightIndex = heights.indexOf(maxHeight);

      // Gap filling disabled with fill-to-height algorithm
      // The distribution algorithm already ensures balanced columns
      // if (this.enableGapFilling && this.shouldFillGaps(heights, maxHeightIndex)) {
      //   this.fillGapsWithPosts(heights);
      //   return;
      // }

      // Only expand shorter columns to match the tallest
      this.columnElements.forEach((column, colIndex) => {
        const currentHeight = heights[colIndex];
        const deficit = maxHeight - currentHeight;

        // If this column is shorter, expand its captions
        if (deficit > 20) {
          this.expandColumnCaptions(column, deficit);
        }
      });

      // Fine-tune after first expansion
      setTimeout(() => this.finetuneBalance(), 100);
    }, 50);
  }

  /**
   * Fine-tunes the balance after initial adjustments
   */
  finetuneBalance() {
    if (!this.columnElements || this.columnElements.length === 0) return;

    const heights = this.columnElements.map(col => col.offsetHeight);
    const maxHeight = Math.max(...heights);

    // Final pass: expand any remaining short columns
    this.columnElements.forEach((column, colIndex) => {
      const deficit = maxHeight - heights[colIndex];

      if (deficit > 10) {
        this.expandColumnCaptions(column, deficit);
      }
    });
  }

  /**
   * Expands captions in a column to add height
   * Only expands, never shrinks below 3 lines
   * @param {HTMLElement} column - Column element
   * @param {number} heightNeeded - Pixels to add
   */
  expandColumnCaptions(column, heightNeeded) {
    const captions = column.querySelectorAll('.ig-masonry-caption-text');
    if (captions.length === 0) return;

    // Calculate lines to add (22px per line with padding)
    const lineHeight = 22;
    const totalLinesNeeded = Math.ceil(heightNeeded / lineHeight);
    const linesPerCaption = Math.ceil(totalLinesNeeded / captions.length);

    if (linesPerCaption === 0) return;

    // Expand each caption
    captions.forEach(caption => {
      const currentClamp = parseInt(caption.style.webkitLineClamp) || 3;
      const newClamp = Math.min(currentClamp + linesPerCaption, 20);

      if (newClamp > currentClamp) {
        caption.style.webkitLineClamp = newClamp.toString();
      }
    });
  }

  /**
   * Checks if we should fill gaps with additional posts
   * Returns true if:
   * 1. There's a significant height gap (>100px)
   * 2. All captions in tallest column are at minimum (3 lines)
   * 3. We have unused posts available
   * @param {Array<number>} heights - Column heights
   * @param {number} maxHeightIndex - Index of tallest column
   * @returns {boolean}
   */
  shouldFillGaps(heights, maxHeightIndex) {
    // Check if we have unused posts
    const hasUnusedPosts = this.displayedPostIndices.size < this.allPosts.length;
    if (!hasUnusedPosts) return false;

    // Check for significant height gap
    const maxHeight = heights[maxHeightIndex];
    const minHeight = Math.min(...heights);
    const gap = maxHeight - minHeight;

    if (gap < 100) return false; // Gap not significant enough

    // Check if tallest column has all captions at minimum
    const tallestColumn = this.columnElements[maxHeightIndex];
    const captions = tallestColumn.querySelectorAll('.ig-masonry-caption-text');

    if (captions.length === 0) return true; // No captions, safe to add posts

    // Check if all captions are at minimum 3 lines
    const allAtMinimum = Array.from(captions).every(caption => {
      const clamp = parseInt(caption.style.webkitLineClamp) || 3;
      return clamp === 3;
    });

    return allAtMinimum;
  }

  /**
   * Fills gaps by adding additional posts to shorter columns
   * @param {Array<number>} heights - Current column heights
   */
  fillGapsWithPosts(heights) {
    // Get unused posts
    const unusedPosts = this.allPosts.filter((_, index) =>
      !this.displayedPostIndices.has(index)
    );

    if (unusedPosts.length === 0) return;

    const maxHeight = Math.max(...heights);

    // Find columns that need filling (those significantly shorter)
    const columnsToFill = heights
      .map((height, index) => ({ height, index }))
      .filter(({ height }) => (maxHeight - height) > 80)
      .sort((a, b) => a.height - b.height); // Shortest first

    if (columnsToFill.length === 0) return;

    // Add posts to shorter columns
    let postsAdded = 0;
    let columnIndex = 0;

    for (let i = 0; i < unusedPosts.length && columnIndex < columnsToFill.length; i++) {
      const postIndex = this.allPosts.indexOf(unusedPosts[i]);
      const post = this.allPosts[postIndex];
      const targetColumn = columnsToFill[columnIndex].index;

      // Create and add the item
      const item = this.createMasonryItem(post, postIndex);
      this.columnElements[targetColumn].appendChild(item);
      this.displayedPostIndices.add(postIndex);

      postsAdded++;

      // Move to next column in rotation
      columnIndex = (columnIndex + 1) % columnsToFill.length;

      // Stop if we've added 2 posts per short column (to avoid overfilling)
      if (postsAdded >= columnsToFill.length * 2) break;
    }

    // If we added posts, wait for images to load and rebalance
    if (postsAdded > 0) {
      const newImages = this.masonryContainer.querySelectorAll('img:not([data-loaded])');
      newImages.forEach(img => img.setAttribute('data-loaded', 'true'));

      setTimeout(() => {
        // Rebalance after new posts are rendered
        this.balanceColumnHeights();
      }, 200);
    }
  }

  /**
   * Adds responsive styles to container
   * @param {HTMLElement} container - Container element
   */
  addResponsiveStyles(container) {
    const styleId = 'ig-widget-masonry-responsive';

    // Remove existing style if present
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;

    // Calculate responsive columns
    const mediumColumns = Math.max(2, Math.floor(this.columns * 0.66));
    const smallColumns = Math.max(1, Math.floor(this.columns * 0.33));

    style.textContent = `
      @media (max-width: 768px) {
        .ig-widget-masonry-column {
          width: calc((100% - ${(mediumColumns - 1) * this.gap}px) / ${mediumColumns}) !important;
        }
        .ig-widget-masonry-column:nth-child(n+${mediumColumns + 1}) {
          display: none !important;
        }
      }
      @media (max-width: 480px) {
        .ig-widget-masonry-column {
          width: calc((100% - ${(smallColumns - 1) * this.gap}px) / ${smallColumns}) !important;
        }
        .ig-widget-masonry-column:nth-child(n+${smallColumns + 1}) {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Recalculates layout (useful for window resize)
   * @param {Array} posts - Instagram posts data
   * @param {HTMLElement} container - Container element
   */
  recalculate(posts, container) {
    // Clear existing content
    container.innerHTML = '';

    // Re-render
    const newMasonry = this.render(posts, container);
    container.appendChild(newMasonry);
  }
}

export default MasonryLayout;
